/**
 * Onboarding API クライアント
 *
 * バックエンドの /v1/onboarding/* および /v1/analytics/onboarding エンドポイントと通信する。
 */

import { apiService } from './api';
import type { Workspace } from '../types/workspace';

export interface ProfileData {
  full_name?: string;
  company?: string;
  role?: string;
}

export interface WorkspaceDraft {
  name: string;
  use_case: string;
  domain: string;
  is_default?: boolean;
  dashboard_layout?: Record<string, unknown>;
}

export interface CompleteOnboardingRequest {
  profile?: ProfileData;
  workspaces: WorkspaceDraft[];
  agent_preferences?: Record<string, unknown>;
}

export interface CompleteOnboardingResponse {
  workspaces: Array<Pick<Workspace, 'id' | 'name' | 'use_case' | 'is_default'>>;
  onboarding_completed_at: string;
  current_workspace_id: string;
}

export interface OnboardingProgressResponse {
  saved: boolean;
  step: string;
}

export type OnboardingEvent =
  | 'onboarding_started'
  | 'onboarding_step_completed'
  | 'onboarding_step_skipped'
  | 'onboarding_completed'
  | 'onboarding_abandoned'
  | 'tour_started'
  | 'tour_completed'
  | 'tour_skipped'
  | 'workspace_created'
  | 'workspace_switched'
  | 'workspace_deleted';

export const onboardingApi = {
  /**
   * オンボーディングを完了する。
   * プロフィール情報とワークスペース設定を送信する。
   */
  async complete(payload: CompleteOnboardingRequest): Promise<CompleteOnboardingResponse> {
    return apiService.fetch<CompleteOnboardingResponse>('/v1/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * オンボーディングの進捗を保存する。
   */
  async progress(step: string, data: Record<string, unknown>): Promise<OnboardingProgressResponse> {
    return apiService.fetch<OnboardingProgressResponse>('/v1/onboarding/progress', {
      method: 'POST',
      body: JSON.stringify({ step, data }),
    });
  },

  /**
   * オンボーディングの分析イベントを記録する。
   *
   * fire-and-forget 用途のため、ネットワークエラーや 401 は握りつぶす。
   * （バグ1 の refresh interceptor で 401 は自動修復されるが、
   *   refresh 失敗時もコンソールノイズにならないよう catch する）
   */
  async recordEvent(
    event: OnboardingEvent,
    step?: number,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      await apiService.fetch<void>('/v1/analytics/onboarding', {
        method: 'POST',
        body: JSON.stringify({ event, step, metadata }),
      });
    } catch {
      // analytics イベントの失敗はサイレントに処理する（ユーザー操作をブロックしない）
    }
  },
};
